import React, { FC, useContext, useState } from 'react';

import { Typography } from '@mui/material';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { UserInfoCtx } from '../../../../containers/CommonLayout/CommonLayout';
import styled from '../../../../styled';
import { colorsPalette, SMALL_MARGIN_PX, useIsWidthUp } from '../../../../theme';
import useIsIframed from '../../../../utils/hooks/useIsIframed';
import { PROFILE_LINKS } from '../../const';

interface UserInfoDropdownProps {
  teamName?: string | null;
  scrolled: boolean;
  handleOpenUserComWidget: () => void;
  handleLogout: () => void;
  onClick: () => void;
}

const avatarColors = [
  { ids: ['0', '1'], background: colorsPalette.coral, color: colorsPalette.white },
  { ids: ['2', '3'], background: colorsPalette.blue, color: colorsPalette.white },
  { ids: ['4', '5'], background: colorsPalette.blueLight, color: colorsPalette.white },
  { ids: ['6', '7'], background: colorsPalette.yellow, color: colorsPalette.blackSecondary },
  { ids: ['8', '9'], background: colorsPalette.pink, color: colorsPalette.blackSecondary },
];

const UserInfoDropdown: FC<UserInfoDropdownProps> = ({
  teamName,
  scrolled,
  handleOpenUserComWidget,
  handleLogout,
  onClick,
}) => {
  const { t } = useTranslation();
  const isMobile = !useIsWidthUp('xl');
  const { userInfo } = useContext(UserInfoCtx);
  const userName = userInfo?.name;

  const [isDropdownOpen, setDropdownOpen] = useState<boolean>(false);
  const isIframed = useIsIframed();

  const colorItem = avatarColors.find(
    (item) => userInfo?.id && item.ids.includes(userInfo?.id?.slice(-1)),
  );

  const toggleDropdown = () => {
    setDropdownOpen((prevState) => !prevState);
  };

  const closeDropdown = () => setDropdownOpen(false);

  const logoutHandler = () => {
    closeDropdown();
    handleLogout();
  };

  const handleClickContact = () => {
    handleOpenUserComWidget();
    closeDropdown();
  };

  return (
    <ClickAwayListener onClickAway={closeDropdown}>
      <DotsBtnWrapper>
        <ProfileAvatar
          aria-label="account of current user"
          onClick={isMobile ? onClick : toggleDropdown}
          background={colorItem?.background}
          color={colorItem?.color}
        >
          {userName
            ?.split(' ')
            .map((item) => item.substr(0, 1))
            .join('')}
        </ProfileAvatar>
        <Dropdown aria-label="dropdown" opened={isDropdownOpen} scrolled={scrolled}>
          <DropdownContent>
            <Divider>
              <Username className="userInfoDropdown_username">{userName}</Username>
              {teamName && <Team>{teamName}</Team>}
            </Divider>
            <LinksWrapper>
              {PROFILE_LINKS.map((item) => (
                <DropdownLink key={item.url} to={item.url} onClick={closeDropdown}>
                  {t(item.title)}
                </DropdownLink>
              ))}
              {
                // @ts-ignore
                !!window?.userengage && (
                  <DropdownLinkDiv onClick={handleClickContact}>
                    {t('header.contact')}
                  </DropdownLinkDiv>
                )
              }
            </LinksWrapper>
          </DropdownContent>
          {!isIframed && (
            <GeneratedLogout>
              <DropdownLinkButton onClick={logoutHandler}>{t('header.logout')}</DropdownLinkButton>
            </GeneratedLogout>
          )}
        </Dropdown>
      </DotsBtnWrapper>
    </ClickAwayListener>
  );
};

export default UserInfoDropdown;

const ProfileAvatar = styled('button', {
  shouldForwardProp: (prop) => prop !== 'background' && prop !== 'color',
})`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  font-weight: 700;
  font-size: 14px;
  background: ${({ background }) => background};
  color: ${({ color }) => color};
  line-height: 17px;
  cursor: pointer;

  ${({ theme }) => theme.breakpoints.up('md')} {
    width: 54px;
    height: 54px;
    font-size: 16px;
  }
`;
const DotsBtnWrapper = styled('div')`
  margin-left: 0;
  position: relative;
  display: block;
`;
const Dropdown = styled('div', {
  shouldForwardProp: (prop) => prop !== 'opened' && prop !== 'scrolled',
})`
  border-radius: 10px;
  background: ${({ theme }) => theme.palette.secondary.light};
  z-index: 1150;
  box-shadow: 0px 13px 26px ${alpha(colorsPalette.blackPure, 0.3)};
  min-width: 240px;
  max-width: 240px;
  position: absolute;
  right: -2px;
  top: ${({ scrolled }) => (scrolled ? '70px' : '80px')};
  transition: all 0.5s ease-in-out;
  display: none;
  ${({ theme }) => theme.breakpoints.up('md')} {
    display: ${({ opened }) => (opened ? 'block' : 'none')};
  }
`;
const DropdownContent = styled('div')`
  position: relative;
  &::before {
    content: '';
    display: block;
    width: 18px;
    height: 18px;
    background: ${({ theme }) => theme.palette.secondary.light};
    transform: rotate(45deg);
    position: absolute;
    top: -9px;
    right: calc(50% - 9px);
    ${({ theme }) => theme.breakpoints.up('sm')} {
      right: 20px;
    }
  }
`;
const Divider = styled('div')`
  position: relative;
  padding: 24px 24px ${SMALL_MARGIN_PX};
  margin-bottom: 9px;
  &::after {
    content: '';
    display: block;
    width: calc(100% - 48px);
    height: 1px;
    background: ${colorsPalette.white};
    opacity: 0.33;
    position: absolute;
    bottom: 0;
  }
  & .userInfoDropdown_username:not(:only-child) {
    margin-bottom: 10px;
  }
  & .userInfoDropdown_username:only-child {
    margin-bottom: 0;
  }
`;
const Username = styled(Typography)`
  && {
    color: ${colorsPalette.white};
    font-size: 18px;
    line-height: 1.2;
    font-weight: bold;
    margin-bottom: 5px;
    display: none;
    ${({ theme }) => theme.breakpoints.up('xl')} {
      display: block;
    }
  }
`;
const Team = styled(Typography)`
  font-size: 14px;
  color: ${colorsPalette.gray};
  line-height: 1.5;
`;
const LinksWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  &:last-child {
    margin-bottom: ${SMALL_MARGIN_PX};
  }
`;
const GeneratedLogout = styled('div')`
  margin-top: ${SMALL_MARGIN_PX};
  padding: 9px 0;
  background: ${colorsPalette.violetAnalyticsCard};
  border-radius: 0 0 10px 10px;
  display: flex;
`;
const createDropdownLinkStyles = (component) => styled(component)`
  border: none;
  background: transparent;
  text-transform: uppercase;
  color: ${colorsPalette.white};
  font-weight: bold;
  font-size: 14px;
  padding: 9px 24px;
  width: 100%;
  transition: background 0.3s;
  cursor: pointer;
  &:hover {
    background: ${colorsPalette.violetAnalyticsCard};
  }
  &:last-child {
    margin-bottom: 0;
  }
`;
const DropdownLink = createDropdownLinkStyles(Link);
const DropdownLinkButton = createDropdownLinkStyles('button');
const DropdownLinkDiv = createDropdownLinkStyles('div');
